import { queryOptions } from "@tanstack/react-query"
import type {
  GroupChatDetail,
  GroupChatListResponse,
  GroupChatMessagesResponse,
  CreateGroupChatRequest,
  SendGroupMessageRequest,
  AddParticipantRequest,
} from "@/types/groupChatTypes"

// Query options for listing all group chats
// Live chats by default; archived=true returns the permanent record of closed
// ones, which are read-only (their Twilio conversation no longer exists).
export const groupChatsQueryOptions = (archived = false) =>
  queryOptions({
    queryKey: ['groupChats', archived ? 'archived' : 'active'],
    queryFn: async (): Promise<GroupChatListResponse> => {
      const response = await fetch(`/api/groupChats${archived ? '?archived=true' : ''}`)
      if (!response.ok) {
        throw new Error('Failed to fetch group chats')
      }
      return response.json()
    },
  })

// Query options for getting a single group chat
export const groupChatQueryOptions = (chatId: string) =>
  queryOptions({
    queryKey: ['groupChat', chatId],
    queryFn: async (): Promise<GroupChatDetail> => {
      const response = await fetch(`/api/groupChats/${chatId}`)
      if (!response.ok) {
        throw new Error('Failed to fetch group chat')
      }
      return response.json()
    },
    enabled: !!chatId,
  })

// Query options for getting messages with polling
export const chatMessagesQueryOptions = (chatId: string) =>
  queryOptions({
    queryKey: ['chatMessages', chatId],
    queryFn: async (): Promise<GroupChatMessagesResponse> => {
      const response = await fetch(`/api/groupChats/${chatId}/messages?limit=100`)
      if (!response.ok) {
        throw new Error('Failed to fetch messages')
      }
      return response.json()
    },
    enabled: !!chatId,
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  })

// Mutation functions (not query options, but useful helpers)
export const createGroupChat = async (data: CreateGroupChatRequest): Promise<GroupChatDetail> => {
  const response = await fetch('/api/groupChats', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create group chat')
  }
  return response.json()
}

export const sendGroupMessage = async (chatId: string, data: SendGroupMessageRequest): Promise<void> => {
  const response = await fetch(`/api/groupChats/${chatId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to send message')
  }
}

export const addParticipant = async (chatId: string, data: AddParticipantRequest): Promise<void> => {
  const response = await fetch(`/api/groupChats/${chatId}/participants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to add participant')
  }
}

export const removeParticipant = async (chatId: string, phoneNumber: string): Promise<void> => {
  const encodedPhone = encodeURIComponent(phoneNumber)
  const response = await fetch(`/api/groupChats/${chatId}/participants/${encodedPhone}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to remove participant')
  }
}

export const archiveGroupChat = async (chatId: string): Promise<void> => {
  const response = await fetch(`/api/groupChats/${chatId}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to archive group chat')
  }
}
